import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FiMoreHorizontal } from 'react-icons/fi';
import { useNote } from '@/hooks/useNote';
import { useClient } from 'jsonapi-react';
import { toast } from 'react-hot-toast';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { confirm } from '@/lib/confirm';
import { useSession } from '@/hooks/useSession';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import usePermission from '@/hooks/usePermission';
import { NotesBtn, NotesBtnHeader, NotesBtnDate, NotesTag, NotesBtnBody, NotesBtnText, NotesAuthor } from './notes';
import { Button } from '@/components/ui/button';
import { FormField } from '../form';
import { FieldGroup } from '@/components/ui/field';
import { IconWrapper } from '../icon';
import { Badge } from '@/components/ui/badge';
import { AvatarWrapper } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export function NotesCard(props) {
  const { note, index, activeNote, setActiveNote, user } = props;
  const [editModal, setEditModal] = React.useState(false);
  const [editNoteTitle, setEditNoteTitle] = React.useState('');
  const client = useClient();
  const { t } = useTranslation();
  const { session } = useSession();
  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });

  const { deleteNote, getNotes, activeTab } = useNote();

  const isAuthUser = session.user.id === user.id;

  const isMarked = note.bookmarked;

  const importantClassroom = () => {
    if (activeTab === 'anotacoes-importantes') {
      return true;
    }
  };

  const editNoteForm = note => {
    setActiveNote(note);
    setEditNoteTitle(note.title);
    setEditModal(true);
  };

  const editCurrentNote = async e => {
    e.preventDefault();

    const { data, error } = await client.mutate(['notes', activeNote.id], {
      title: editNoteTitle
    });
    if (error) {
      toast.error('Anotação não pode ser editada!');
    } else {
      setActiveNote({ ...activeNote, title: data.title });
      let newListEdit = note;
      newListEdit.title = data.title;
      toast.success('Anotação editada!');
    }
    setEditModal(false);
    setActiveNote(null);
    setEditNoteTitle('');
    getNotes(note?.classroom);
  };

  async function handleViewNote(note) {
    if (note?.new_note) {
      const { data } = await client.fetch(['notes', note.id], {
        new_note: false
      });
      getNotes(note?.classroom);
    }
  }

  const confirmDeleteNote = async note => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta anotação?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      deleteNote(note, note.classroom).catch(error => {
        console.error(error);
      });
    }
  };

  return (
    <div
      onClick={() => {
        handleViewNote(note);
      }}
    >
      <Sheet
        open={editModal}
        onOpenChange={() => {
          setEditModal(false);
        }}
      >
        <SheetContent>
          <SheetHeader />
          <SheetBody>
            <form
              method="patch"
              onSubmit={editCurrentNote}
            >
              <FieldGroup>
                <SheetTitle>Editar anotação</SheetTitle>

                <FormField
                  label={t('textsCommon.name')}
                  name="note"
                  type="text"
                  placeholder="Digite o nome da anotação"
                  value={editNoteTitle}
                  onChange={e => setEditNoteTitle(e.target.value)}
                />

                <Button
                  className="self-start"
                  type="submit"
                >
                  Salvar
                </Button>
              </FieldGroup>
            </form>
          </SheetBody>
          <SheetFooter />
        </SheetContent>
      </Sheet>

      <NotesBtn
        key={`${note}-${index}`}
        active={activeNote?.id === note?.id}
        onClick={() => setActiveNote(note)}
      >
        <NotesBtnHeader>
          <NotesBtnDate>
            {t('projects.updatedIn')} {note['updated_at']}
          </NotesBtnDate>
          {(isAuthUser || isAdmin) && (usePermission('Editar anotações', 'Anotações') || usePermission('Excluir anotações', 'Anotações')) && (
            <div onClick={e => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconWrapper>
                    <FiMoreHorizontal />
                  </IconWrapper>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="z-[9999]"
                >
                  {usePermission('Editar anotações', 'Anotações') && <DropdownMenuItem onSelect={() => editNoteForm(note)}>Editar nome</DropdownMenuItem>}
                  {usePermission('Excluir anotações', 'Anotações') && <DropdownMenuItem onSelect={() => confirmDeleteNote(note)}>{t('button.delete')}</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {isMarked && (
            <NotesTag>
              <svg
                width="10"
                height="15"
                viewBox="0 0 10 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 10.75V1C0 0.447715 0.447716 0 1 0H9C9.55229 0 10 0.447715 10 1V10.75C10 11.0648 9.85181 11.3611 9.6 11.55L5.6 14.55C5.24444 14.8167 4.75556 14.8167 4.4 14.55L0.4 11.55C0.148194 11.3611 0 11.0648 0 10.75Z"
                  fill="#FDB462"
                />
              </svg>
            </NotesTag>
          )}
        </NotesBtnHeader>
        <NotesBtnBody>
          <NotesBtnText>{note.title}</NotesBtnText>

          {note?.new_note && (
            <Badge
              variant="warning"
              size="xs"
            >
              Nova
            </Badge>
          )}
          {importantClassroom() && <span className="expanded-calendar__tag expanded-calendar__tag--class">{note.classroom.title}</span>}
        </NotesBtnBody>

        <NotesAuthor>
          <AvatarWrapper
            size="xs"
            src={user?.image}
            text={user?.name}
          />
          <span>{user?.name}</span>
        </NotesAuthor>
      </NotesBtn>
    </div>
  );
}
